import type { Nullable } from "models-webgl2";
import { makeAutoBindFunctions } from "utils/helpers/make-auto-bind-functions";
import { throttle } from "utils/helpers/throttle";
import { download } from "utils/helpers/download";
import { Webgl2Tool } from "../tools";
import { Webgl2dRenderer } from "../webgl2d-renderer";
import { rendererWheelThrottlingTimeout } from "../webgl2d-renderer.constants";

export class EventsManager {
  private get _canvas() {
    return this._renderer.canvas;
  }

  private get _camera() {
    return this._renderer.camera;
  }

  private _lastMouseEvent: Nullable<MouseEvent>;
  private _lastTouchEvent: Nullable<TouchEvent>;
  private _isMouseDown = false;
  private _isTouchDown = false;

  public get isInMouseTouchEvent() {
    return this._isMouseDown || this._isTouchDown;
  }

  private _waitingSync = false;

  private _webgl2Tool: Nullable<Webgl2Tool> = null;

  constructor(private readonly _renderer: Webgl2dRenderer) {
    makeAutoBindFunctions(this);
  }

  public checkForTool(tool: Webgl2Tool): boolean {
    return tool === this._webgl2Tool;
  }

  public assignTool(tool: Webgl2Tool) {
    if (tool === this._webgl2Tool) return;

    this.unassignTool();

    this._webgl2Tool = tool;
    this._webgl2Tool.onAssign?.();

    this._renderer.render();
  }

  public unassignTool() {
    if (this._webgl2Tool) {
      if (this._isMouseDown && this._lastMouseEvent) {
        this._webgl2Tool.onMouseCanceled?.(this._lastMouseEvent);
        this._webgl2Tool.onMouseEnded?.(this._lastMouseEvent);
      }

      if (this._isTouchDown && this._lastTouchEvent) {
        this._webgl2Tool.onTouchCanceled?.(this._lastTouchEvent);
        this._webgl2Tool.onTouchEnded?.(this._lastTouchEvent);
      }

      // Fix recursive issue if you try change tool inside another tool
      const toUnassign = this._webgl2Tool;
      this._webgl2Tool = null;
      toUnassign.onUnAssign?.();
    }

    this._lastMouseEvent = null;
    this._lastTouchEvent = null;
    this._isMouseDown = false;
    this._isTouchDown = false;
    this._webgl2Tool = null;
    this.removeMouseListeners();

    this._renderer.render();
  }

  public registerListeners() {
    this._canvas.addEventListener("mousedown", this._handleMouseDown);
    this._canvas.addEventListener("mouseleave", this._handleMouseDownLeave);

    this._canvas.addEventListener("mouseenter", this._handleMouseEnter);
    this._canvas.addEventListener("mouseleave", this._handleMouseLeave);
    this._canvas.addEventListener("mousemove", this._handleMouseOver, { passive: true });

    this._canvas.addEventListener("touchstart", this._handleTouchStart);
    this._canvas.addEventListener("touchmove", this._handleTouchMove);
    this._canvas.addEventListener("touchend", this._handleTouchEnd);
    this._canvas.addEventListener("touchcancel", this._handleTouchCancel);

    window.addEventListener("keydown", this._handleWindowKeyDown);
    this._canvas.addEventListener("keydown", this._handleKeyDown);

    window.addEventListener("wheel", this._handleWindowWheel, { passive: false });
    this._canvas.addEventListener("wheel", this._handleWheel);
    this._canvas.addEventListener("contextmenu", this._handleContextMenu);
  }

  public unregisterListeners() {
    this._canvas.removeEventListener("mousedown", this._handleMouseDown);
    this._canvas.removeEventListener("mouseleave", this._handleMouseDownLeave);

    this._canvas.removeEventListener("mouseenter", this._handleMouseEnter);
    this._canvas.removeEventListener("mouseleave", this._handleMouseLeave);
    this._canvas.removeEventListener("mousemove", this._handleMouseOver);

    this._canvas.removeEventListener("touchstart", this._handleTouchStart);
    this._canvas.removeEventListener("touchmove", this._handleTouchMove);
    this._canvas.removeEventListener("touchend", this._handleTouchEnd);
    this._canvas.removeEventListener("touchcancel", this._handleTouchCancel);

    window.removeEventListener("keydown", this._handleWindowKeyDown);
    this._canvas.removeEventListener("keydown", this._handleKeyDown);

    window.removeEventListener("wheel", this._handleWindowWheel);
    this._canvas.removeEventListener("wheel", this._handleWheel);
    this._canvas.removeEventListener("contextmenu", this._handleContextMenu);
  }

  private removeMouseListeners() {
    window.removeEventListener("mousemove", this._handleMouseMove);
    window.removeEventListener("mouseup", this._handleMouseUp);
  }

  // Mouse events
  private _handleMouseDown(e: MouseEvent) {
    this._renderer.canvas.focus();
    e.preventDefault();

    if (!this._webgl2Tool) return;

    const result = this._webgl2Tool.onMouseDown?.(e);
    if (result === false) return;

    this._lastMouseEvent = e;
    this._isMouseDown = true;

    window.addEventListener("mousemove", this._handleMouseMove);
    window.addEventListener("mouseup", this._handleMouseUp);
  }

  private _handleMouseMove(e: MouseEvent) {
    if (!this._isMouseDown || !this._webgl2Tool) return;
    if (!this._waitingSync) {
      this._waitingSync = true;
      this._webgl2Tool.onMouseMove?.(e);
      window.requestAnimationFrame(() => {
        this._waitingSync = false;
      });
    }
    this._lastMouseEvent = e;
  }

  private _handleMouseUp(e: MouseEvent) {
    if (!this._isMouseDown || !this._webgl2Tool) return;

    this._webgl2Tool.onMouseUp?.(e);
    this._webgl2Tool.onMouseEnded?.(e);

    this._lastMouseEvent = null;
    this._isMouseDown = false;
    this.removeMouseListeners();
  }

  private _handleMouseDownLeave(e: MouseEvent) {
    if (!this._isMouseDown || !this._webgl2Tool) return;

    this._webgl2Tool.onMouseLeave?.(e);
    this._webgl2Tool.onMouseEnded?.(e);

    this._lastMouseEvent = null;
    this._isMouseDown = false;
    this.removeMouseListeners();
  }

  private _handleMouseEnter(e: MouseEvent) {
    if (!this._webgl2Tool) return;
    this._webgl2Tool.onMouseEnter?.(e);
  }

  private _handleMouseLeave(e: MouseEvent) {
    if (!this._webgl2Tool) return;
    this._webgl2Tool.onMouseLeave?.(e);
  }

  private _waitingMouseOverSync = false;

  private _handleMouseOver(e: MouseEvent) {
    if (!this._webgl2Tool) return;

    if (!this._waitingMouseOverSync) {
      this._waitingMouseOverSync = true;
      this._webgl2Tool.onMouseOver?.(e);
      window.requestAnimationFrame(() => {
        this._waitingMouseOverSync = false;
      });
    }
  }

  // Touch events
  private _handleTouchStart(e: TouchEvent) {
    e.preventDefault();

    if (!this._webgl2Tool) return;

    const result = this._webgl2Tool.onTouchStart?.(e);
    if (result === false) return;

    this._lastTouchEvent = e;
    this._isTouchDown = true;
  }

  private _handleTouchMove(e: TouchEvent) {
    if (!this._isTouchDown || !this._webgl2Tool) return;
    if (!this._waitingSync) {
      this._waitingSync = true;
      this._webgl2Tool.onTouchMove?.(e);
      window.requestAnimationFrame(() => {
        this._waitingSync = false;
      });
    }
    this._lastTouchEvent = e;
  }

  private _handleTouchEnd(e: TouchEvent) {
    if (!this._isTouchDown || !this._webgl2Tool) return;

    this._webgl2Tool.onTouchEnd?.(e);
    this._webgl2Tool.onTouchEnded?.(e);

    this._lastTouchEvent = null;
    this._isTouchDown = false;
  }

  private _handleTouchCancel(e: TouchEvent) {
    if (!this._isTouchDown || !this._webgl2Tool) return;

    this._webgl2Tool.onTouchCanceled?.(e);
    this._webgl2Tool.onTouchEnded?.(e);

    this._lastTouchEvent = null;
    this._isTouchDown = false;
  }

  // Keyboard events
  private _handleWindowKeyDown(e: KeyboardEvent) {
    // NOTE: removes shortcuts for page zooming in browsers
    if (
      e.ctrlKey &&
      (e.code === "Equal" ||
        e.code === "Minus" ||
        e.code === "NumpadSubtract" ||
        e.code === "NumpadAdd")
    ) {
      e.preventDefault();
    }
  }

  private _handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Tab") return;

    e.preventDefault();

    if (e.code === "KeyZ" && e.shiftKey && (e.metaKey || e.ctrlKey)) {
      this._renderer.commands.redo();
      return;
    }

    if (e.code === "KeyZ" && (e.metaKey || e.ctrlKey)) {
      this._renderer.commands.undo();
      return;
    }

    if (e.code === "KeyC" && e.shiftKey && (e.metaKey || e.ctrlKey)) {
      this._renderer.zoomToCenter();
      return;
    }

    if (e.code === "KeyD" && e.shiftKey && (e.metaKey || e.ctrlKey)) {
      download(this._renderer.export.getCanvasCast().toDataURL(), "canvas-cast");
      return;
    }
  }

  // Wheel events
  private _handleWheelScale = throttle((e: WheelEvent) => {
    this._camera.rescale(e);
  }, rendererWheelThrottlingTimeout);

  private _handleWindowWheel(e: WheelEvent) {
    e.ctrlKey && e.preventDefault();
  }

  private _handleWheel(e: WheelEvent) {
    e.preventDefault();

    if (this.isInMouseTouchEvent) return;

    if (e.ctrlKey || e.metaKey) {
      this._handleWheelScale(e);
      return;
    }

    const moveY = e.shiftKey ? 0 : e.deltaY;
    const moveX = e.shiftKey ? e.deltaX || e.deltaY : e.deltaX || 0;
    this._camera.translate(moveX, moveY, true);
  }

  // Misc events
  private _handleContextMenu(e: MouseEvent) {
    e.preventDefault();
  }
}
